import { supabase } from "../lib/supabase";
import { authService } from "./authService";
import { getCurrentPlatform } from "../utils/platform";
import {
  buildAccreditationTypes,
  buildPaymentDiscountPayload,
  buildPaymentMetadata,
  getPaymentPartnerId,
} from "../utils/paymentPayloads";

// supabase-js hides non-2xx bodies behind FunctionsHttpError.context (a
// Response). Read it once so callers can act on structured error payloads.
const readEdgeFunctionErrorBody = async (error) => {
  try {
    return (await error?.context?.json?.()) || null;
  } catch {
    return null;
  }
};

class StripeService {
  constructor() {
    this.processingPayments = new Set(); // Track payments being processed
  }

  async createCheckoutSession(orderData) {
    try {
      // Payment record is created server-side by the stripe-payment edge function
      // to avoid duplicate orphaned records. Do NOT create one here.

      // Ensure the Supabase client has a fresh token before invoking the edge function
      await authService.ensureFreshToken();

      // Verify we have an active session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error("Please sign in to create payment");
      }

      // Verify the user ID matches (only when user_id is explicitly provided)
      if (orderData.user_id && session.user.id !== orderData.user_id) {
        console.error("User ID mismatch:", {
          sessionUserId: session.user.id,
          paymentDataUserId: orderData.user_id,
        });
        throw new Error("User authentication mismatch");
      }

      // Build payment data — handle both cart purchases (from CartDrawer)
      // and single course purchases (from course detail pages)
      const isCartPurchase = !!(orderData?.courseIds || orderData?.bundleIds);
      const amount = orderData?.totalAmount ?? orderData?.amount;
      const partnerId = getPaymentPartnerId(orderData);
      const accreditationTypes = buildAccreditationTypes(orderData);
      const metadata = buildPaymentMetadata({
        ...orderData,
        partnerId,
        accreditationTypes,
      });
      const discountPayload = buildPaymentDiscountPayload(orderData);

      const paymentData = {
        amount,
        currency: orderData?.currency || "usd",
        description: orderData?.description || `Course Enrollment`,
        course_id: orderData?.course_id,
        enrollment_id: orderData?.enrollment_id,
        customer_name: orderData?.customer_name || "",
        customer_email: orderData?.customer_email || "",
        customer_phone: orderData?.customer_phone || "",
        platform: getCurrentPlatform(),
        ...discountPayload,
        ...(partnerId ? { partner_id: partnerId } : {}),
        metadata:
          isCartPurchase || Object.keys(metadata).length > 0
            ? metadata
            : undefined,
      };

      // Use supabase.functions.invoke() which handles auth internally
      // (attaches the current session's access_token automatically)
      const { data: sessionData, error } = await supabase.functions.invoke(
        "stripe-payment",
        {
          body: { paymentData },
        },
      );

      if (error) {
        console.error("Stripe Edge Function error:", error);
        const body = await readEdgeFunctionErrorBody(error);
        const msg = body?.error || error?.message || error?.context?.body || "";
        if (
          typeof msg === "string" &&
          (msg.includes("401") ||
            msg.toLowerCase().includes("unauthorized") ||
            msg.toLowerCase().includes("jwt"))
        ) {
          throw new Error("Unauthorized - Please sign in again and try");
        }
        const sessionError = new Error(
          typeof msg === "string" && msg
            ? msg
            : "Failed to create payment session",
        );
        // The 409 already-owned response names the offending courses so the
        // cart can say which line (or bundle) to remove.
        if (Array.isArray(body?.owned_course_ids)) {
          sessionError.ownedCourseIds = body.owned_course_ids;
        }
        // 409: the same course is in the cart twice (alone and in a bundle).
        if (Array.isArray(body?.overlapping_course_ids)) {
          sessionError.overlappingCourseIds = body.overlapping_course_ids;
        }
        throw sessionError;
      }

      return {
        ...sessionData,
        payment_id: sessionData?.payment_id,
      };
    } catch (error) {
      console.error("Stripe checkout session creation error:", error);
      const wrapped = new Error(
        error?.message || "Failed to create payment session",
      );
      if (error?.ownedCourseIds) wrapped.ownedCourseIds = error.ownedCourseIds;
      if (error?.overlappingCourseIds) {
        wrapped.overlappingCourseIds = error.overlappingCourseIds;
      }
      throw wrapped;
    }
  }

  async handleCheckoutRedirect(sessionId, paymentId) {
    try {
      // console.log removed('Handling Stripe checkout redirect:', { sessionId, paymentId });

      if (!paymentId) {
        console.error("Missing payment ID");
        throw new Error("Payment ID is required");
      }

      // Check if payment is already being processed
      if (this.processingPayments.has(paymentId)) {
        throw new Error("Payment is already being processed");
      }

      // Add to processing set
      this.processingPayments.add(paymentId);

      // The account this confirmation runs as: the success page only touches
      // the cart of that same signed-in account.
      const session = await authService.getValidSession();
      const { data, error } = await supabase.functions.invoke(
        "confirm-stripe-payment",
        {
          body: {
            sessionId,
            paymentId,
            siteUrl: window.location.origin,
          },
        },
      );

      if (error) {
        // The edge function's JSON body says why (not paid yet, refunded,
        // invalid session…); supabase-js only exposes a generic message.
        const body = await readEdgeFunctionErrorBody(error);
        const confirmError = new Error(
          body?.error || error?.message || "Payment verification failed",
        );
        confirmError.status = error?.context?.status ?? null;
        confirmError.refunded = body?.refunded === true;
        // Stripe reported the session paid; only access is still missing.
        confirmError.paymentReceived = body?.payment_received === true;
        throw confirmError;
      }

      return {
        success: true,
        payment: data?.payment,
        verified: true,
        sessionId,
        paymentId,
        notifications: data?.notifications,
        // What the server's payment record says was bought. An older
        // function that omits them yields [], so nothing is removed.
        courseIds: Array.isArray(data?.course_ids) ? data.course_ids : [],
        bundleIds: Array.isArray(data?.bundle_ids) ? data.bundle_ids : [],
        ownerId: session?.user?.id || null,
      };
    } catch (error) {
      console.error("Stripe checkout redirect handling error:", error);
      const wrapped = new Error(
        error?.message || "Payment verification failed",
      );
      if (error?.status) wrapped.status = error.status;
      if (error?.refunded) wrapped.refunded = true;
      if (error?.paymentReceived) wrapped.paymentReceived = true;
      throw wrapped;
    } finally {
      // Always remove from processing set
      if (paymentId) {
        this.processingPayments.delete(paymentId);
      }
    }
  }

  /**
   * Reports an abandoned/failed checkout attempt back to the caller. It does
   * NOT cancel the payment — Stripe's own session expiry and the
   * `checkout.session.expired` webhook own that.
   */
  async handlePaymentFailure(sessionId, paymentId, errorDetails) {
    return {
      success: false,
      error: errorDetails,
      retry_available: true,
      session_id: sessionId,
      payment_id: paymentId,
    };
  }
}

export const stripeService = new StripeService();
